// Refunds.jsx - Política de Cambios y Devoluciones para MediExamPro
import React from 'react';
import { 
  ArrowLeft, Shield, Clock, AlertCircle, CheckCircle, 
  XCircle, RefreshCw, CreditCard, Mail, Phone, FileText,
  Info, Ban, Calendar, DollarSign
} from 'lucide-react';

const Refunds = () => {
  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <button 
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-blue-200 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </button>
          
          <h1 className="text-4xl font-bold mb-4">
            Política de Cambios y Devoluciones
          </h1>
          <p className="text-blue-100 text-lg">
            Tu satisfacción es nuestra prioridad. Conoce nuestras políticas claras y transparentes.
          </p>
          <p className="text-sm text-blue-200 mt-4">
            Última actualización: Enero 2025 | Versión 2.0
          </p>
        </div>
      </div>

      {/* Navegación rápida */}
      <div className="bg-white shadow-sm sticky top-0 z-10 border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <button 
              onClick={() => scrollToSection('garantia')}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Garantía 7 días
            </button>
            <button 
              onClick={() => scrollToSection('proceso')}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Proceso de devolución
            </button>
            <button 
              onClick={() => scrollToSection('condiciones')}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Condiciones
            </button>
            <button 
              onClick={() => scrollToSection('contacto')}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Contacto
            </button>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        
        {/* Información importante */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <Info className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-lg font-bold text-blue-900 mb-2">
                Información Importante sobre Devoluciones
              </h2>
              <p className="text-blue-800">
                MediExamPro es un servicio digital educativo de suscripción. De acuerdo con el 
                Código de Protección y Defensa del Consumidor del Perú (Ley N° 29571), ofrecemos 
                una <strong>garantía de satisfacción de 7 días calendario</strong> desde la fecha 
                de compra para solicitar la devolución completa de tu dinero.
              </p>
            </div>
          </div>
        </div>

        {/* Sección 1: Garantía de 7 días */}
        <section id="garantia" className="mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-green-100 p-3 rounded-full">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Garantía de Satisfacción de 7 Días
              </h2>
            </div>

            <div className="space-y-4">
              <p className="text-gray-700">
                Estamos tan seguros de la calidad de nuestro servicio que ofrecemos una 
                <strong> garantía de devolución total del dinero durante los primeros 7 días calendario</strong> después 
                de tu compra.
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  ¿Cómo funciona?
                </h3>
                <ul className="space-y-2 text-green-800">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">•</span>
                    <span>Si no estás satisfecho con el servicio, puedes solicitar la devolución completa dentro de los primeros 7 días.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">•</span>
                    <span>No necesitas dar explicaciones, aunque agradecemos tu feedback para mejorar.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">•</span>
                    <span>El reembolso se procesará en un máximo de 10 días hábiles.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">•</span>
                    <span>Recibirás el 100% del monto pagado en el mismo método de pago utilizado.</span>
                  </li>
                </ul>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mt-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    Ejemplo de plazos
                  </h4>
                  <p className="text-sm text-gray-700">
                    Si compraste el 1 de enero a las 10:00 AM, tienes hasta el 8 de enero 
                    a las 10:00 AM para solicitar tu devolución.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    Monto de devolución
                  </h4>
                  <p className="text-sm text-gray-700">
                    Se devuelve el 100% del monto pagado, incluyendo impuestos. 
                    Sin descuentos ni penalizaciones.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sección 2: Proceso de devolución */}
        <section id="proceso" className="mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-100 p-3 rounded-full">
                <RefreshCw className="h-6 w-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Proceso de Solicitud de Devolución
              </h2>
            </div>

            <div className="space-y-6">
              <p className="text-gray-700">
                Hemos simplificado el proceso para que puedas solicitar tu devolución de manera rápida y sencilla:
              </p>

              {/* Pasos del proceso */}
              <div className="space-y-4">
                {[
                  {
                    step: '1',
                    title: 'Contacta con nosotros',
                    description: 'Envía un email a mediexampro@gmail.com o escríbenos por WhatsApp al +51 997 268 543 indicando tu solicitud de devolución.',
                    icon: Mail,
                    time: 'Inmediato'
                  },
                  {
                    step: '2',
                    title: 'Proporciona información',
                    description: 'Incluye tu número de orden, email de registro y fecha de compra. Opcionalmente, cuéntanos por qué no cumplimos tus expectativas.',
                    icon: FileText,
                    time: '2 minutos'
                  },
                  {
                    step: '3',
                    title: 'Confirmación',
                    description: 'Recibirás una confirmación de tu solicitud en máximo 24 horas hábiles con el número de caso.',
                    icon: CheckCircle,
                    time: '24 horas'
                  },
                  {
                    step: '4',
                    title: 'Procesamiento',
                    description: 'Procesaremos tu devolución y cancelaremos tu suscripción. Tu acceso continuará hasta el final del período pagado.',
                    icon: Clock,
                    time: '1-3 días hábiles'
                  },
                  {
                    step: '5',
                    title: 'Reembolso',
                    description: 'El dinero será devuelto al mismo método de pago utilizado. Verás el reembolso reflejado según los tiempos de tu banco.',
                    icon: CreditCard,
                    time: '5-10 días hábiles'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                        {item.step}
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                        {item.title}
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                          {item.time}
                        </span>
                      </h3>
                      <p className="text-gray-700">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Información adicional */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                <h3 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Importante sobre los tiempos
                </h3>
                <ul className="space-y-1 text-sm text-yellow-800">
                  <li>• El tiempo de procesamiento comienza cuando recibimos tu solicitud completa.</li>
                  <li>• Los reembolsos en tarjetas de crédito pueden tardar 5-10 días hábiles en reflejarse.</li>
                  <li>• Los reembolsos en Yape o Plin son inmediatos una vez procesados.</li>
                  <li>• Te notificaremos por email cada paso del proceso.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Sección 3: Condiciones y excepciones */}
        <section id="condiciones" className="mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-orange-100 p-3 rounded-full">
                <AlertCircle className="h-6 w-6 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Condiciones y Excepciones
              </h2>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Casos donde SÍ aplica la devolución
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Solicitud dentro de los primeros 7 días calendario desde la compra.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Problemas técnicos persistentes que impidan el uso del servicio.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Cobros duplicados o erróneos (devolución del monto duplicado).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Contenido significativamente diferente al descrito.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Imposibilidad de acceder al servicio por causas atribuibles a MediExamPro.</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  Casos donde NO aplica la devolución
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Solicitudes después de los 7 días calendario desde la compra.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Uso extensivo del servicio (más del 50% del contenido consumido).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Violación de los términos de servicio o uso indebido.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Cambio de opinión después del período de garantía.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Problemas de conectividad a internet del usuario.</span>
                  </li>
                </ul>
              </div>

              {/* Política de uso justo */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Ban className="h-5 w-5 text-gray-600" />
                  Política de Uso Justo
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  Para prevenir el abuso del sistema de devoluciones:
                </p>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Los usuarios que soliciten devoluciones repetidas pueden ser bloqueados del servicio.</li>
                  <li>• Nos reservamos el derecho de rechazar futuras suscripciones de usuarios que abusen del sistema.</li>
                  <li>• Monitoreamos el uso para detectar patrones de abuso.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Sección 4: Cambios y cancelaciones */}
        <section className="mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-purple-100 p-3 rounded-full">
                <RefreshCw className="h-6 w-6 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Cambios de Plan y Cancelaciones
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Cambios de Plan</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Puedes cambiar tu plan en cualquier momento desde tu panel de usuario.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Los cambios a un plan superior son inmediatos y se cobra la diferencia prorrateada.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Los cambios a un plan inferior se aplican al final del período de facturación actual.</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Cancelación de Suscripción</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Puedes cancelar tu suscripción en cualquier momento sin penalizaciones.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Mantendrás acceso hasta el final del período pagado.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>No se realizan devoluciones parciales por tiempo no utilizado después de 7 días.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Puedes reactivar tu suscripción en cualquier momento.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Sección 5: Información de contacto */}
        <section id="contacto" className="mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-8 text-white">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-white/20 p-3 rounded-full">
                <Phone className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold">
                ¿Necesitas Ayuda con tu Devolución?
              </h2>
            </div>

            <p className="text-blue-100 mb-6">
              Nuestro equipo de soporte está disponible para ayudarte con cualquier consulta sobre devoluciones:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-lg p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email
                </h3>
                <a href="mailto:mediexampro@gmail.com" className="text-blue-100 hover:text-white">
                  mediexampro@gmail.com
                </a>
                <p className="text-sm text-blue-200 mt-1">
                  Respuesta en 24 horas hábiles
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  WhatsApp
                </h3>
                <a href="https://wa.me/51997268543" className="text-blue-100 hover:text-white">
                  +51 997 268 543
                </a>
                <p className="text-sm text-blue-200 mt-1">
                  Lun-Vie 9AM-6PM | Sáb 9AM-1PM
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-white/10 rounded-lg">
              <p className="text-sm">
                <strong>Para solicitudes de devolución, incluye:</strong>
              </p>
              <ul className="text-sm text-blue-100 mt-2 space-y-1">
                <li>• Número de orden o ID de transacción</li>
                <li>• Email utilizado en el registro</li>
                <li>• Fecha de compra</li>
                <li>• Motivo de la solicitud (opcional pero apreciado)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Marco legal */}
        <section className="mb-12">
          <div className="bg-gray-100 rounded-xl p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              Marco Legal y Normativo
            </h2>
            <div className="space-y-2 text-sm text-gray-700">
              <p>
                Esta política de devoluciones cumple con:
              </p>
              <ul className="space-y-1 ml-4">
                <li>• Ley N° 29571 - Código de Protección y Defensa del Consumidor del Perú</li>
                <li>• Decreto Supremo N° 011-2011-PCM - Reglamento del Libro de Reclamaciones</li>
                <li>• Normas de INDECOPI sobre comercio electrónico</li>
                <li>• Regulaciones de Culqi para procesamiento de pagos</li>
              </ul>
              <p className="mt-3">
                <strong>Jurisdicción:</strong> Cualquier disputa será resuelta bajo las leyes de la República del Perú.
              </p>
              <p>
                <strong>RUC:</strong> 10709315302 | <strong>Razón Social:</strong> MEDIEXAMPRO
              </p>
            </div>
          </div>
        </section>

        {/* Actualizaciones de la política */}
        <section className="mb-8">
          <div className="bg-blue-50 rounded-xl p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              Actualizaciones de esta Política
            </h2>
            <p className="text-gray-700 text-sm">
              Nos reservamos el derecho de actualizar esta política en cualquier momento. 
              Los cambios serán efectivos inmediatamente después de su publicación en el sitio web. 
              La fecha de última actualización se mostrará al inicio de este documento.
            </p>
            <p className="text-gray-700 text-sm mt-2">
              Te recomendamos revisar periódicamente esta página para estar informado de cualquier cambio.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
};

export default Refunds;